import { Injectable } from '@angular/core';
import { RestApiClientAdapterService } from '../sysbiz/rest-api-client-adapter.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthService } from '@systems/base';
import { TranslateService } from '@ngx-translate/core';
import { BANKING_CONFIG } from '../sysbiz/config-loader.service';
import { Observable, map } from 'rxjs';
import { UploadTransactionResultDto } from './transaction.service';

@Injectable({
  providedIn: 'root'
})
export class BankingCompanyService extends RestApiClientAdapterService<BankingCompanyDto, BankingCompanyDto, BankingCompanyDto> {

  constructor(
    protected override http: HttpClient,
    authService: AuthService,
    protected translationService: TranslateService
  ) {
    super(BANKING_CONFIG, http, authService, 'companies');
  } 

  public details(companyGuid: string
  ): Observable<BankingCompanyDto> {

    return this.http.get<BankingCompanyDto>(this.getUrl(`companies/${companyGuid}`),
      {
        responseType: 'json',
        observe: 'response',
        headers: {
          authenticate: this.authService.authenticationToken ?? ""
        }
      }).pipe(map((resp: any) => {
        this.updateTokenFromResponse(resp);
        return resp.body;
      }));
  }
  public syncTransactions(companyGuid: string
  ): Observable<UploadTransactionResultDto> {
    return this.http.post<UploadTransactionResultDto>(this.getUrl(`companies/${companyGuid}/synchronize`),{},
      {
        responseType: 'json',
        observe: 'response',
        headers: {
          authenticate: this.authService.authenticationToken ?? ""
        }
      }).pipe(map((resp: any) => {
        this.updateTokenFromResponse(resp);
        return resp.body;
      }));
  }

  public startOnboarding(companyGuid: string, onboardingProcess: OnBoardingProcessDto
  ): Observable<BankingCompanyDto> {
    return this.http.post<BankingCompanyDto>(this.getUrl(`companies/${companyGuid}/startonboarding`),onboardingProcess,
      {
        responseType: 'json',
        observe: 'response',
        headers: {
          authenticate: this.authService.authenticationToken ?? ""
        }
      }).pipe(map((resp: any) => {
        this.updateTokenFromResponse(resp);
        return resp.body;
      }));
  }

  public resetOnboarding(companyGuid: string): Observable<boolean> {
    return this.http.put<boolean>(
      this.getUrl(`companies/${companyGuid}/resetonboarding`),
      {}, 
      {
        responseType: 'json',
        observe: 'response',
        headers: {
          authenticate: this.authService.authenticationToken ?? "", 
        },
      }
    ).pipe(
      map((resp: any) => {
        this.updateTokenFromResponse(resp);
        return resp.body;
      })
    );
  }

  public refreshRenewalLink(companyGuid: string): Observable<string> {
    return this.http.put(
      this.getUrl(`companies/${companyGuid}/refreshrenewallink`),
      {},
      {
        responseType: 'text', 
        observe: 'response',
        headers: {
          authenticate: this.authService.authenticationToken ?? "",
        },
      }
    ).pipe(
      map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      })
    );
  }

  public createCompany(companyGuid: string): Observable<BankingCompanyDto> {
    return this.http.post<BankingCompanyDto>(
      this.getUrl('companies'),
      JSON.stringify(companyGuid),
      {
        headers: {
          'Content-Type': 'application/json',
          authenticate: this.authService.authenticationToken ?? "",
        },
        responseType: 'json',
        observe: 'response',
      }
    ).pipe(
      map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      })
    );
  }

  public refreshOnboardingData(companyGuid: string): Observable<BankingCompanyDto> {
      return this.http.put<BankingCompanyDto>(this.getUrl(`companies/${companyGuid}/refreshonboardingdata`),
        {}, 
        {
          responseType: 'json',
          observe: 'response',
          headers: {
            authenticate: this.authService.authenticationToken ?? ""
      }
    }).pipe(map((resp: any) => {
      this.updateTokenFromResponse(resp);
      return resp.body;
    }));
  }

  public updateConfig(companyGuid: string, config: BankingCompanyConfigDto): Observable<BankingCompanyConfigDto> {
    return this.http.put<BankingCompanyConfigDto>(this.getUrl(`companies/${companyGuid}/config`), config,
      {
        responseType: 'json',
        observe: 'response',
        headers: {
          authenticate: this.authService.authenticationToken
        }
      }).pipe(map((resp: any) => {
        this.updateTokenFromResponse(resp);
        return resp.body;
      }));
  }
  
}

export interface BankingCompanyDto {
  companyGuid : string;
  externalUserCompanyCode?: string;
  externalUserId?: string;
  externalUserOnboardingId?: string;
  externalOnboardingState?: string;
  externalOnboardingUrl?: string;
  externalHasValidKyc?: boolean;
  externalUserKycStatus?: string;
  externalHasActiveContract?: boolean;
  externalHasActiveAuthorizations?: boolean;
  notificationCcEmails?: string[];
}

export interface BankingCompanyConfigDto {
  notificationCcEmails?: string[];
}

export interface OnBoardingProcessDto {
  companyUserCode?: string;
  name?: string;
  surname?: string;
  contacts?: ContactDto;
  fiscalCode?: string;
  birthDate?: Date;
  birthPlace?: string;
  homeAddress?: AddressDto;
  currentAddress?: AddressDto;
  startSyncUtc?: Date;
}

export interface AddressDto {
  city?: string;
  country?: string;
  countryId?: number;
  postalCode?: string;
  province?: string | null;
  provinceId?: number;
  buildingNumber?: number;
  address?: string;
}

export interface ContactDto {
  mail?: string;
  mobile?: string;
}


